import { inject, Injectable } from '@angular/core'

import { confirmSignIn, setUpTOTP, updateMFAPreference, verifyTOTPSetup } from 'aws-amplify/auth'

import { ApiService } from '@ti/core/api'
import { AuthSession } from '@ti/core/auth/services'
import { isFleetRole, isTspRole } from '@ti/core/auth/utils'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { PROFILE } from '@ti/core/contracts/profile'
import { LanguageService } from '@ti/core/ui-kit/i18n'

@Injectable({ providedIn: 'root' })
export class MfaClient {
  protected readonly api = inject(ApiService)
  protected readonly authSession = inject(AuthSession)
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly languageService = inject(LanguageService)
  protected readonly profile = inject(PROFILE)

  public async getMfaSetupData(appName: string) {
    const currentUser = await this.authSession.getUserAttributes()
    if (!currentUser) {
      throw new Error('User not authenticated')
    }

    const { sharedSecret, getSetupUri } = await setUpTOTP()
    return {
      sharedSecret,
      setupUri: getSetupUri(appName, currentUser.email || currentUser.name),
    }
  }

  public async verifyMfaSetup(code: string) {
    await verifyTOTPSetup({ code })
    await updateMFAPreference({ totp: 'PREFERRED' })

    const userRoute = await this.getUserRoute()
    await userRoute.refreshOwnMfaStatus()
    await this.profile.refresh()
  }

  public async confirmSignIn(challengeResponse: string) {
    return confirmSignIn({ challengeResponse })
  }

  public async disableSoftwareToken() {
    const confirmed = await this.confirmDisableSoftwareToken()
    if (!confirmed) return

    const userRoute = await this.getUserRoute()
    await userRoute.disableOwnSoftwareTokenMfa()
    await userRoute.refreshOwnMfaStatus()
    await this.profile.refresh()
  }

  protected async getUserRoute() {
    const currentUser = await this.authSession.getUserAttributes()
    if (currentUser && isFleetRole(currentUser.role)) {
      return this.api.fleet.user
    } else if (currentUser && isTspRole(currentUser.role)) {
      return this.api.tsp.user
    } else {
      return this.api.ti.user
    }
  }

  protected async confirmDisableSoftwareToken() {
    const labels = await this.languageService.massTranslate({
      summary: 'users.authenticator.confirm-disable-dialog.summary',
      description: 'users.authenticator.confirm-disable-dialog.description',
      confirmButton: 'common.yes-disable',
    })
    return this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
  }
}
